/**
 * What the composer accepts as an attachment, and how it triages a batch of
 * files before anything is staged.
 *
 * Images keep their own path (compression, thumbnails, inline delivery to the
 * model). Everything else is a "file": it is stored as-is, mirrored into the
 * thread's workspace under `uploads/`, and read there by the agent. The
 * only questions asked here are the ones the server would otherwise refuse
 * later (size, count, emptiness) so a person hears about a problem while
 * the file picker is still open, not after pressing send.
 */
import {
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  PROVIDER_SEND_TURN_MAX_FILE_BYTES,
  PROVIDER_SEND_TURN_MAX_INLINE_FILE_BYTES,
} from "@amoreg/contracts";

/**
 * The picker's `accept` list: images plus every document kind a compliance
 * team hands around. Drops and pastes are not filtered by this (anything
 * within the size cap can be attached); it only shapes the native dialog.
 * HTML is deliberately absent: the server never renders an uploaded page
 * inline, so offering it would only invite a file that downloads.
 */
export const COMPOSER_ATTACHMENT_ACCEPT = [
  "image/*",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".xlsm",
  ".csv",
  ".tsv",
  ".txt",
  ".md",
  ".markdown",
  ".rtf",
  ".odt",
  ".ods",
  ".odp",
  ".ppt",
  ".pptx",
  ".json",
  ".xml",
  ".yaml",
  ".yml",
  ".eml",
  ".msg",
  ".zip",
].join(",");

export const isImageFile = (file: File): boolean => file.type.startsWith("image/");

/** The mime type the attachment is declared with; browsers report "" for unknown kinds. */
export const fileAttachmentMimeType = (file: File): string =>
  file.type.trim().length > 0 ? file.type : "application/octet-stream";

export function partitionComposerFiles(files: readonly File[]): {
  readonly images: File[];
  readonly documents: File[];
} {
  const images: File[] = [];
  const documents: File[] = [];
  for (const file of files) {
    (isImageFile(file) ? images : documents).push(file);
  }
  return { images, documents };
}

export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

/**
 * Accept what fits, in order, and describe the first thing that did not.
 * `reservedCount` is everything already staged (images and files together),
 * because the per-message cap covers both.
 */
export function reviewFileAttachments(input: {
  readonly files: readonly File[];
  readonly reservedCount: number;
  readonly maxBytes?: number;
}): { readonly accepted: File[]; readonly error: string | null } {
  const maxBytes = input.maxBytes ?? PROVIDER_SEND_TURN_MAX_FILE_BYTES;
  const limitLabel = formatAttachmentSize(maxBytes).replace(".0", "");
  const accepted: File[] = [];
  let error: string | null = null;
  let reserved = input.reservedCount;
  for (const file of input.files) {
    if (file.size === 0) {
      error = `'${file.name}' is empty.`;
      continue;
    }
    if (file.size > maxBytes) {
      error = `'${file.name}' is larger than ${limitLabel}, the limit for an attached file.`;
      continue;
    }
    if (reserved >= PROVIDER_SEND_TURN_MAX_ATTACHMENTS) {
      error = `You can attach up to ${PROVIDER_SEND_TURN_MAX_ATTACHMENTS} files per message.`;
      break;
    }
    accepted.push(file);
    reserved += 1;
  }
  return { accepted, error };
}

/** Inline-only question and Inspector forms keep their bounded JSON payloads. */
export const reviewInlineFileAttachments = (input: {
  files: readonly File[];
  reservedCount: number;
}) => reviewFileAttachments({ ...input, maxBytes: PROVIDER_SEND_TURN_MAX_INLINE_FILE_BYTES });
